import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "383",
  "slug": "longest-coastlines-in-the-world-ranked",
  "title": "The Longest Coastlines in the World, Ranked",
  "excerpt": "Canada has more coastline than the next several countries combined, but measuring a coast is trickier than it sounds. Here are the longest coastlines on Earth and the strange reason no two sources agree.",
  "date": "2026-06-09",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1591164115502-09f4edf9f005?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMGNvYXN0bGluZSUyMGNsaWZmc3xlbnwxfDB8fHwxNzgwOTkyODI3fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Canada has by far the longest coastline of any country, followed by Norway, Indonesia, Russia, and the Philippines. Exact figures vary between sources because of the \"coastline paradox\": the more finely you measure a jagged coast, the longer it gets.",
  "content": [
    {
      "type": "paragraph",
      "text": "Some countries are defined by their interiors. Others are almost entirely edge. When you rank nations by the length of their coastlines, the results are full of surprises, and they reveal a genuinely strange truth about geography: there is no single correct length for any coastline. Let us start with the rankings, then explain why they wobble."
    },
    {
      "type": "heading",
      "text": "The Longest Coastlines on Earth"
    },
    {
      "type": "paragraph",
      "text": "By the most widely cited figures, the countries with the longest coastlines are:"
    },
    {
      "type": "list",
      "items": [
        "Canada — roughly 202,000 km, more than any other nation by a wide margin, thanks to its endless Arctic islands and fjorded edges.",
        "Norway — around 100,000 km when you count the deep fjords and thousands of offshore islands.",
        "Indonesia — about 54,000 km spread across some 17,000 islands.",
        "Russia — roughly 37,000 km, most of it along the Arctic Ocean.",
        "Philippines — around 36,000 km across its sprawling archipelago.",
        "Japan, Australia, the United States, Antarctica, and New Zealand — all with tens of thousands of kilometres of coast."
      ]
    },
    {
      "type": "paragraph",
      "text": "Notice the pattern: archipelagos and heavily indented coasts dominate. A country does not need to be large to rank high. The Philippines and Norway are far smaller than Russia, yet their fractured, island-dotted edges give them enormous coastlines."
    },
    {
      "type": "heading",
      "text": "Why Canada Runs Away With It"
    },
    {
      "type": "paragraph",
      "text": "Canada’s lead is almost absurd. Its mainland coast is long enough on its own, but the real driver is the Canadian Arctic Archipelago, a scatter of islands so numerous and so jagged that they multiply the country’s shoreline many times over. Add the deep inlets of British Columbia and the bays of the Atlantic provinces and you get a coastline longer than the distance to the Moon, several times over."
    },
    {
      "type": "heading",
      "text": "The Coastline Paradox"
    },
    {
      "type": "paragraph",
      "text": "Here is where geography gets philosophical. If you measure a coastline with a long ruler, you get one number. Measure the same coast with a shorter ruler that can trace into every bay and around every rock, and you get a longer number. Keep shrinking the ruler and the length keeps growing, in theory without limit. This is the famous coastline paradox, first noted by mathematician Lewis Fry Richardson and later folded into the early study of fractals."
    },
    {
      "type": "paragraph",
      "text": "It is not a measuring error; it is a real property of crinkly natural shapes. A coast has detail at every scale, from the sweep of a gulf down to the grains around a single pebble. That is why two perfectly honest sources can list very different coastline lengths for the same country. They simply chose different rulers."
    },
    {
      "type": "heading",
      "text": "Why the Numbers Disagree"
    },
    {
      "type": "paragraph",
      "text": "When you see conflicting coastline figures, the difference usually comes down to a few choices:"
    },
    {
      "type": "list",
      "items": [
        "Resolution — how detailed the underlying map or satellite data is.",
        "Whether offshore islands are counted, and how many.",
        "How tidal flats, river mouths, and estuaries are treated.",
        "The date of the survey, since coasts erode, silt up, and shift over time."
      ]
    },
    {
      "type": "paragraph",
      "text": "None of these make any single ranking dishonest. They just mean coastline length is a measured quantity with built-in fuzziness, not a fixed fact like a country’s land area."
    },
    {
      "type": "heading",
      "text": "What This Teaches You About Maps"
    },
    {
      "type": "paragraph",
      "text": "The coastline paradox is a useful reminder that maps are models, not the territory. Every map simplifies. The smooth blue line you see tracing a country’s edge is a decision about how much detail to keep. Understanding that helps you read maps and satellite imagery more critically, and it explains why a tiny island nation can punch so far above its weight on a coastline ranking."
    },
    {
      "type": "heading",
      "text": "Smooth Coasts vs. Jagged Coasts"
    },
    {
      "type": "paragraph",
      "text": "The shape of a coastline is itself a clue to its history. Long, smooth, sweeping beaches usually mark low-lying coasts of sand and sediment, where the land slopes gently into the sea. Wildly jagged coasts full of inlets and islands tend to record more dramatic forces: ancient glaciers, drowned river valleys, or rugged uplifted rock. So a high coastline ranking is not just trivia. It is a strong hint that a country is fringed with fjords, islands, or both, which in turn tells you something real about its geology and its climate history."
    }
  ]
};
